/**
 * ConstellationCLIPage — lukhas.dev
 *
 * Full-page self-contained Constellation dashboard. Uses static data with a
 * simulated live feed that cycles through states. No API dependencies.
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Header, HeaderLogo, HeaderNav, HeaderNavLink, HeaderActions, Footer, Button } from '@lukhas/ui'

// ── Design tokens ─────────────────────────────────────────────────────────────

const MONO = `'SF Mono', 'Cascadia Code', 'JetBrains Mono', 'Fira Code', monospace`
const DISPLAY = `'DM Sans', 'Helvetica Neue', -apple-system, sans-serif`

const C = {
  bg:           '#08090C',
  surface:      '#0E1015',
  surfaceHover: '#13161D',
  border:       '#1A1E28',
  borderSubtle: '#14171F',
  accent:       '#C8CCD8',
  text:         '#A0A6B4',
  textDim:      '#5A6070',
  textGhost:    '#333844',
  white:        '#E8ECF2',
  lambda:       'rgba(200, 204, 216, 0.9)',
  lambdaGlow:   'rgba(200, 204, 216, 0.04)',
} as const

// ── Static stars ──────────────────────────────────────────────────────────────

interface StarRow {
  code: string; name: string; status: string; health: number
}

interface ActivityEntry {
  time: string; message: string; type: 'info' | 'success' | 'warn'
}

const BASE_STARS: StarRow[] = [
  { code: 'IDN', name: 'Identity',  status: 'secured',  health: 98 },
  { code: 'MEM', name: 'Memory',    status: 'syncing',   health: 95 },
  { code: 'VIS', name: 'Vision',    status: 'online',    health: 100 },
  { code: 'BIO', name: 'Bio',       status: 'online',    health: 97 },
  { code: 'DRM', name: 'Dream',     status: 'standby',   health: 88 },
  { code: 'ETH', name: 'Ethics',    status: 'watching',  health: 100 },
  { code: 'GRD', name: 'Guardian',  status: 'online',    health: 99 },
  { code: 'QNT', name: 'Quantum',   status: 'standby',   health: 92 },
]

const PIPELINE = [
  { letter: 'M', label: 'Memory' },
  { letter: 'A', label: 'Attention' },
  { letter: 'T', label: 'Thought' },
  { letter: 'R', label: 'Risk' },
  { letter: 'I', label: 'Intent' },
  { letter: 'Z', label: 'Action' },
]

const LOG_POOL: ActivityEntry[] = [
  { time: '', message: 'constellation health check passed',       type: 'success' },
  { time: '', message: 'identity token refreshed — sovereign',   type: 'success' },
  { time: '', message: 'ethics watchdog sweep complete',          type: 'success' },
  { time: '', message: 'dream engine entering standby',           type: 'info'    },
  { time: '', message: 'memory fold operation completed',         type: 'info'    },
  { time: '', message: 'guardian drift-check nominal',            type: 'success' },
  { time: '', message: 'quantum coherence within bounds',         type: 'info'    },
  { time: '', message: 'bio adaptation cycle triggered',          type: 'info'    },
]

function nowTime(): string {
  return new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
}

// ── Small hooks ───────────────────────────────────────────────────────────────

function useStagger(count: number, base = 80, start = 600): boolean[] {
  const [vis, setVis] = useState<boolean[]>(Array(count).fill(false))
  useEffect(() => {
    const ts = Array.from({ length: count }, (_, i) =>
      setTimeout(() => setVis(prev => { const n = [...prev]; n[i] = true; return n }), start + i * base)
    )
    return () => ts.forEach(clearTimeout)
  }, [count, base, start])
  return vis
}

// ── Sub-components ────────────────────────────────────────────────────────────

function LambdaMark() {
  return (
    <div style={{ position: 'relative', width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: -20, borderRadius: '50%', background: `radial-gradient(circle, ${C.lambdaGlow} 0%, transparent 70%)`, animation: 'dccl-breathe 4s ease-in-out infinite' }} />
      <span style={{ fontFamily: DISPLAY, fontSize: 38, fontWeight: 300, color: C.lambda, letterSpacing: '-0.02em', position: 'relative', zIndex: 1 }}>&#923;</span>
    </div>
  )
}

function MetricCell({ label, value, unit = '', visible = true, delay = 0 }: { label: string; value: string; unit?: string; visible?: boolean; delay?: number }) {
  return (
    <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(8px)', transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${delay}ms` }}>
      <div style={{ fontFamily: MONO, fontSize: 10, color: C.textDim, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
        <span style={{ fontFamily: MONO, fontSize: 20, fontWeight: 400, color: C.white, letterSpacing: '-0.02em' }}>{value}</span>
        {unit && <span style={{ fontFamily: MONO, fontSize: 10, color: C.textDim }}>{unit}</span>}
      </div>
    </div>
  )
}

function StarRowItem({ code, name, status, health, visible = true, idx = 0 }: StarRow & { visible?: boolean; idx?: number }) {
  const [hovered, setHovered] = useState(false)
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'grid', gridTemplateColumns: '52px 1fr 90px 64px 28px',
        alignItems: 'center', padding: '14px 20px', cursor: 'default',
        background: hovered ? C.surfaceHover : 'transparent',
        borderBottom: `1px solid ${C.borderSubtle}`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : 'translateX(-12px)',
        transition: `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${200 + idx * 70}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${200 + idx * 70}ms, background 0.2s ease`,
      }}
    >
      <span style={{ fontFamily: MONO, fontSize: 11, fontWeight: 500, color: C.accent, letterSpacing: '0.06em' }}>{code}</span>
      <span style={{ fontFamily: DISPLAY, fontSize: 14, color: hovered ? C.white : C.text, transition: 'color 0.2s' }}>{name}</span>
      <span style={{ fontFamily: MONO, fontSize: 10, color: C.textDim, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{status}</span>
      <div style={{ height: 2, background: C.border, borderRadius: 1, overflow: 'hidden' }}>
        <div style={{ width: `${health}%`, height: '100%', background: C.accent, opacity: 0.5, borderRadius: 1, transition: 'width 1s cubic-bezier(0.16,1,0.3,1)' }} />
      </div>
      <span style={{ fontFamily: MONO, fontSize: 10, color: C.textDim, textAlign: 'right', opacity: hovered ? 1 : 0, transition: 'opacity 0.2s' }}>&#8594;</span>
    </div>
  )
}

function SectionLabel({ children, visible = true, delay = 0 }: { children: React.ReactNode; visible?: boolean; delay?: number }) {
  return (
    <div style={{
      fontFamily: MONO, fontSize: 10, color: C.textGhost,
      letterSpacing: '0.14em', textTransform: 'uppercase',
      padding: '0 20px', marginBottom: 2,
      opacity: visible ? 1 : 0, transition: `opacity 0.5s ease ${delay}ms`,
    }}>{children}</div>
  )
}

// ── Page component ────────────────────────────────────────────────────────────

export default function ConstellationCLIPage() {
  const [mounted, setMounted] = useState(false)
  const [headerVis, setHeaderVis] = useState(false)
  const [metricsVis, setMetricsVis] = useState(false)
  const [activeStage, setActiveStage] = useState(2)
  const [cycle, setCycle] = useState(48291)
  const [entropy, setEntropy] = useState(0.0023)
  const [log, setLog] = useState<ActivityEntry[]>(() =>
    LOG_POOL.slice(0, 5).map((e, i) => ({ ...e, time: `00:0${i}:${22 + i * 4}` }))
  )

  useEffect(() => {
    setMounted(true)
    const t1 = setTimeout(() => setHeaderVis(true), 200)
    const t2 = setTimeout(() => setMetricsVis(true), 500)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [])

  useEffect(() => {
    const id = setInterval(() => {
      setActiveStage(prev => (prev + 1) % PIPELINE.length)
      setCycle(prev => prev + 1)
      setEntropy(prev => Math.max(0.0001, prev + (Math.random() - 0.5) * 0.0002))
      const pick = LOG_POOL[Math.floor(Math.random() * LOG_POOL.length)]
      setLog(prev => [{ ...pick, time: nowTime() }, ...prev].slice(0, 5))
    }, 5000)
    return () => clearInterval(id)
  }, [])

  const starsVis = useStagger(BASE_STARS.length, 70, 800)
  const pipeVis = useStagger(PIPELINE.length, 100, 400)
  const actVis = useStagger(log.length, 60, 1000)

  const metrics = [
    { label: 'Modules', value: '692', unit: 'loaded' },
    { label: 'Uptime',  value: '99.99', unit: '%' },
    { label: 'Entropy', value: entropy.toFixed(4) },
    { label: 'Cycle',   value: `#${cycle.toLocaleString()}` },
  ]

  return (
    <div className="min-h-screen bg-consciousness-deep text-awareness-silver">
      <Header className="fixed top-0 left-0 right-0 z-50 bg-consciousness-deep/80 backdrop-blur-md border-b border-code-cyan/20">
        <HeaderLogo href="/">
          <span className="text-2xl font-light tracking-[0.15em] text-awareness-silver">
            LUKHAS<span className="text-code-cyan">.DEV</span>
          </span>
        </HeaderLogo>
        <HeaderNav>
          <HeaderNavLink href="/dashboard">Dashboard</HeaderNavLink>
          <HeaderNavLink href="/docs">Docs</HeaderNavLink>
          <HeaderNavLink href="/playground">Playground</HeaderNavLink>
        </HeaderNav>
        <HeaderActions>
          <a href="https://lukhas.id/login" target="_blank" rel="noopener noreferrer">
            <Button variant="ghost">Sign In</Button>
          </a>
        </HeaderActions>
      </Header>

      {/* Page header */}
      <section className="pt-24 pb-8 px-6">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-white/70 transition-colors mb-8"
            style={{ fontFamily: '"Helvetica Neue", sans-serif', fontWeight: 300 }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <h1
            className="text-4xl md:text-5xl text-white tracking-[0.12em] mb-4"
            style={{ fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", sans-serif', fontWeight: 100 }}
          >
            &#923; Constellation Dashboard
          </h1>
          <p className="text-lg text-white/55 max-w-2xl" style={{ fontFamily: '"Helvetica Neue", sans-serif', fontWeight: 300 }}>
            Live view of the 8-star Constellation framework — identity, memory, vision, bio, dream, ethics, guardian, and quantum subsystems.
          </p>
        </div>
      </section>

      {/* CLI panel */}
      <section className="px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          <div style={{ background: C.bg, color: C.text, fontFamily: DISPLAY, position: 'relative', overflow: 'hidden', borderRadius: 2 }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '32px 32px 0', opacity: headerVis ? 1 : 0, transform: headerVis ? 'translateY(0)' : 'translateY(-10px)', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <LambdaMark />
                <div>
                  <div style={{ fontFamily: DISPLAY, fontSize: 18, fontWeight: 300, color: C.white, letterSpacing: '0.08em' }}>LUKH&#923;S</div>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: C.textDim, letterSpacing: '0.12em', marginTop: 2 }}>CONSCIOUSNESS BY DESIGN</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: C.textGhost, letterSpacing: '0.1em' }}>MODE</div>
                  <div style={{ fontFamily: MONO, fontSize: 11, color: C.textDim, marginTop: 2 }}>simulated</div>
                </div>
                <div style={{ width: 1, height: 28, background: C.border }} />
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: C.textGhost, letterSpacing: '0.1em' }}>TIER</div>
                  <div style={{ fontFamily: MONO, fontSize: 11, color: C.accent, marginTop: 2 }}>SOVEREIGN</div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: C.border, margin: '24px 32px', opacity: headerVis ? 1 : 0, transform: headerVis ? 'scaleX(1)' : 'scaleX(0)', transformOrigin: 'left', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 400ms' }} />

            {/* Metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, margin: '0 32px 24px', background: C.border, border: `1px solid ${C.border}` }}>
              {metrics.map((m, i) => (
                <div key={m.label} style={{ background: C.surface, padding: '18px 20px' }}>
                  <MetricCell {...m} visible={metricsVis} delay={i * 80} />
                </div>
              ))}
            </div>

            {/* Main grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 1, margin: '0 32px', background: C.border, border: `1px solid ${C.border}` }}>
              {/* Left: constellation table */}
              <div style={{ background: C.surface }}>
                <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${C.borderSubtle}` }}>
                  <SectionLabel visible={mounted} delay={600}>Constellation</SectionLabel>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '52px 1fr 90px 64px 28px', alignItems: 'center', padding: '8px 20px', borderBottom: `1px solid ${C.borderSubtle}`, opacity: mounted ? 1 : 0, transition: 'opacity 0.4s ease 700ms' }}>
                  {['Code', 'Module', 'Status', 'Health', ''].map(h => (
                    <span key={h} style={{ fontFamily: MONO, fontSize: 9, color: C.textGhost, letterSpacing: '0.12em', textTransform: 'uppercase' }}>{h}</span>
                  ))}
                </div>
                {BASE_STARS.map((s, i) => (
                  <StarRowItem key={s.code} {...s} idx={i} visible={starsVis[i]} />
                ))}
              </div>

              {/* Right: pipeline + activity */}
              <div style={{ background: C.surface, display: 'flex', flexDirection: 'column' }}>
                {/* Pipeline */}
                <div style={{ padding: '16px 20px 20px', borderBottom: `1px solid ${C.borderSubtle}` }}>
                  <SectionLabel visible={mounted} delay={300}>MATRIZ Pipeline</SectionLabel>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 16, position: 'relative' }}>
                    <div style={{ position: 'absolute', top: 18, left: 18, right: 18, height: 1, background: C.border, zIndex: 0 }} />
                    <div style={{ position: 'absolute', top: 18, left: 18, width: mounted ? `${(activeStage / (PIPELINE.length - 1)) * 100}%` : '0%', height: 1, background: C.textDim, zIndex: 0, transition: 'width 1s cubic-bezier(0.16,1,0.3,1)' }} />
                    {PIPELINE.map((s, i) => (
                      <div key={s.letter} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, opacity: pipeVis[i] ? 1 : 0, transform: pipeVis[i] ? 'translateY(0) scale(1)' : 'translateY(6px) scale(0.95)', transition: `all 0.5s cubic-bezier(0.16,1,0.3,1) ${400 + i * 100}ms` }}>
                        <div style={{ width: 36, height: 36, border: `1.5px solid ${i === activeStage ? C.accent : i < activeStage ? C.textDim : C.border}`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontSize: 13, fontWeight: 500, color: i === activeStage ? C.white : i < activeStage ? C.textDim : C.textGhost, background: i === activeStage ? `${C.accent}0A` : 'transparent', transition: 'all 0.6s ease', position: 'relative' }}>
                          {s.letter}
                          {i === activeStage && <div style={{ position: 'absolute', inset: -6, borderRadius: '50%', border: `1px solid ${C.accent}15`, animation: 'dccl-pulse 3s ease-in-out infinite' }} />}
                        </div>
                        <span style={{ fontFamily: MONO, fontSize: 9, color: i === activeStage ? C.text : C.textGhost, letterSpacing: '0.1em', textTransform: 'uppercase', transition: 'color 0.3s' }}>{s.label}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, padding: '10px 0 0', borderTop: `1px solid ${C.borderSubtle}`, opacity: mounted ? 1 : 0, transition: 'opacity 0.5s ease 1000ms' }}>
                    <span style={{ fontFamily: MONO, fontSize: 10, color: C.textGhost }}>CYCLE</span>
                    <span style={{ fontFamily: MONO, fontSize: 10, color: C.textDim }}>#{cycle.toLocaleString()}</span>
                  </div>
                </div>

                {/* Activity log */}
                <div style={{ flex: 1, padding: '16px 20px' }}>
                  <SectionLabel visible={mounted} delay={800}>Activity</SectionLabel>
                  <div style={{ marginTop: 8 }}>
                    {log.map((a, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '68px 1fr', gap: 12, padding: '7px 0', opacity: actVis[i] ? 1 : 0, transform: actVis[i] ? 'translateY(0)' : 'translateY(4px)', transition: `all 0.4s cubic-bezier(0.16,1,0.3,1) ${600 + i * 60}ms` }}>
                        <span style={{ fontFamily: MONO, fontSize: 11, color: C.textGhost }}>{a.time}</span>
                        <span style={{ fontFamily: MONO, fontSize: 11, color: a.type === 'success' ? C.text : C.textDim }}>{a.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Read-only prompt */}
            <div style={{ margin: '0 32px', borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, background: C.surface }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '14px 20px', gap: 10, opacity: mounted ? 1 : 0, transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1) 1200ms' }}>
                <span style={{ fontFamily: MONO, fontSize: 12, color: C.textDim }}>$</span>
                <div style={{ flex: 1, position: 'relative', borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
                  <span style={{ fontFamily: MONO, fontSize: 12, color: C.textGhost }}>use lukhas.io/console or lukhas.team for interactive terminal access...</span>
                  <span style={{ display: 'inline-block', width: 1, height: 14, background: C.accent, marginLeft: 2, verticalAlign: 'middle', animation: 'dccl-blink 1.2s steps(1) infinite', opacity: 0.6 }} />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 32px 24px', opacity: mounted ? 1 : 0, transition: 'opacity 0.5s ease 1400ms' }}>
              <span style={{ fontFamily: MONO, fontSize: 9, color: C.textGhost, letterSpacing: '0.1em' }}>
                LUKH&#923;S v4.2.1 — 8-STAR CONSTELLATION FRAMEWORK
              </span>
              <span style={{ fontFamily: MONO, fontSize: 9, color: C.textGhost, letterSpacing: '0.1em' }}>
                692 MODULES — SIMULATED
              </span>
            </div>

            <style>{`
              @keyframes dccl-breathe {
                0%, 100% { transform: scale(1); opacity: 0.6; }
                50%       { transform: scale(1.15); opacity: 1; }
              }
              @keyframes dccl-pulse {
                0%, 100% { transform: scale(1); opacity: 0.4; }
                50%       { transform: scale(1.3); opacity: 0; }
              }
              @keyframes dccl-blink {
                0%, 100% { opacity: 0.6; }
                50%       { opacity: 0; }
              }
            `}</style>
          </div>

          {/* Info callout */}
          <div className="mt-8 p-6 rounded-xl border border-vision-teal/20 bg-vision-teal/5">
            <p className="text-sm text-white/60" style={{ fontFamily: '"Helvetica Neue", sans-serif', fontWeight: 300 }}>
              This is a simulated view. For live constellation data connected to the LUKHAS backend, visit{' '}
              <a href="https://lukhas.io/console" className="text-vision-teal hover:text-vision-cyan transition-colors">lukhas.io/console</a>{' '}
              or the{' '}
              <a href="https://team.lukhas.ai/team/dashboard" className="text-vision-teal hover:text-vision-cyan transition-colors">LUKHAS team portal</a>.
              Interactive terminal access requires authentication via{' '}
              <a href="https://lukhas.id" className="text-vision-teal hover:text-vision-cyan transition-colors">lukhas.id</a>.
            </p>
          </div>
        </div>
      </section>

      <Footer activeDomain="lukhas.dev">
        <div>
          <h3 className="text-sm font-semibold text-awareness-silver mb-3">Developer Resources</h3>
          <ul className="space-y-2">
            <li><a href="/docs" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Documentation</a></li>
            <li><a href="/playground" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Playground</a></li>
            <li><a href="/constellation" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Constellation CLI</a></li>
          </ul>
        </div>
      </Footer>
    </div>
  )
}
